import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getActiveCookieEvidence,
  getLatestDanmakuEvidence,
  getSentimentAnalysis,
  type ActiveCookieEvidence,
  type LatestDanmakuEvidence,
} from '@/lib/api';
import {
  getCaptureSegment,
  getDataHealthSegment,
  getDataSegment,
  getLoginSegment,
  getSentimentSegment,
  type MonitorSegmentState,
  type MonitorTone,
} from '@/lib/monitorStatus';
import type { LiveSession, SentimentAnalysisResult } from '@/types';
import { cn } from '@/lib/utils';

interface RealtimeStatusPanelProps {
  sessions: LiveSession[];
  refreshNonce?: number;
}

interface SourceErrors {
  cookie: boolean;
  data: boolean;
  sentiment: boolean;
}

interface SegmentDefinition extends MonitorSegmentState {
  title: string;
  code: string;
}

const toneStyles: Record<MonitorTone, { dot: string; text: string }> = {
  healthy: { dot: 'bg-[var(--status-healthy)]', text: 'text-[var(--status-healthy)]' },
  warning: { dot: 'bg-[var(--status-warning)]', text: 'text-[var(--status-warning)]' },
  live: { dot: 'bg-[var(--status-live)]', text: 'text-[var(--status-live)]' },
  error: { dot: 'bg-[var(--status-error)]', text: 'text-[var(--status-error)]' },
  unknown: { dot: 'bg-[var(--status-unknown)]', text: 'text-[var(--status-unknown)]' },
  action: { dot: 'bg-[var(--status-action)]', text: 'text-[var(--status-action)]' },
};

const unavailableState: MonitorSegmentState = {
  label: '无法确认',
  detail: '状态证据暂不可用',
  tone: 'error',
};

export default function RealtimeStatusPanel({
  sessions,
  refreshNonce = 0,
}: RealtimeStatusPanelProps) {
  const activeSession = useMemo(
    () =>
      sessions.find((session) => session.status === 'live' && session.is_recording) ??
      sessions.find((session) => session.status === 'live') ??
      null,
    [sessions]
  );

  const [cookie, setCookie] = useState<ActiveCookieEvidence | null>(null);
  const [latestDanmaku, setLatestDanmaku] = useState<LatestDanmakuEvidence | null>(null);
  const [analysis, setAnalysis] = useState<SentimentAnalysisResult | null>(null);
  const [sourceErrors, setSourceErrors] = useState<SourceErrors>({
    cookie: false,
    data: false,
    sentiment: false,
  });
  const [isRefreshing, setIsRefreshing] = useState(true);
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);

  const refreshEvidence = useCallback(async (showLoading = false) => {
    if (showLoading) setIsRefreshing(true);

    const [cookieResult, dataResult, sentimentResult] = await Promise.allSettled([
      getActiveCookieEvidence(),
      activeSession ? getLatestDanmakuEvidence(activeSession.id) : Promise.resolve(null),
      activeSession ? getSentimentAnalysis(activeSession.id) : Promise.resolve(null),
    ] as const);

    if (cookieResult.status === 'fulfilled') setCookie(cookieResult.value);
    if (dataResult.status === 'fulfilled') setLatestDanmaku(dataResult.value);
    if (sentimentResult.status === 'fulfilled') setAnalysis(sentimentResult.value);

    setSourceErrors({
      cookie: cookieResult.status === 'rejected',
      data: dataResult.status === 'rejected',
      sentiment: sentimentResult.status === 'rejected',
    });
    setLastCheckedAt(new Date());
    setIsRefreshing(false);
  }, [activeSession]);

  useEffect(() => {
    void refreshEvidence(true);
  }, [refreshEvidence, refreshNonce]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void refreshEvidence(false);
    }, 30_000);
    return () => window.clearInterval(intervalId);
  }, [refreshEvidence]);

  const now = lastCheckedAt?.getTime() ?? Date.now();
  const segments: SegmentDefinition[] = [
    {
      code: 'AUTH',
      title: '登录状态',
      ...(sourceErrors.cookie ? unavailableState : getLoginSegment(cookie, now)),
    },
    { code: 'CAPTURE', title: '采集状态', ...getCaptureSegment(sessions) },
    {
      code: 'DATA',
      title: '数据状态',
      ...(sourceErrors.data
        ? unavailableState
        : getDataSegment(activeSession, latestDanmaku, now)),
    },
    {
      code: 'HEALTH',
      title: '数据健康度',
      ...(sourceErrors.data
        ? unavailableState
        : getDataHealthSegment(activeSession, latestDanmaku, now)),
    },
    {
      code: 'ANALYSIS',
      title: '舆情分析',
      ...(sourceErrors.sentiment
        ? unavailableState
        : getSentimentSegment(activeSession, analysis)),
    },
  ];

  return (
    <section
      aria-labelledby="realtime-status-title"
      className="console-panel bg-[var(--status-panel)]"
      data-status-segments={segments.length}
    >
      <h2 id="realtime-status-title" className="sr-only">实时分段状态</h2>
      <p className="sr-only" aria-live="polite">
        {lastCheckedAt
          ? '状态最近检查于 ' + lastCheckedAt.toLocaleTimeString('zh-CN')
          : '正在读取状态证据'}
      </p>

      <div className="grid lg:grid-cols-5" aria-busy={isRefreshing && !lastCheckedAt}>
        {segments.map((segment, index) => {
          const styles = toneStyles[segment.tone];
          return (
            <div
              key={segment.code}
              className={cn(
                'grid min-h-16 grid-cols-[minmax(96px,auto)_1fr_auto] items-center gap-3 border-b border-[var(--border-subtle)] px-3 py-2.5 last:border-b-0 lg:block lg:min-h-[100px] lg:border-b-0 lg:px-5 lg:py-4',
                index > 0 && 'lg:border-l'
              )}
            >
              <div className="flex items-center gap-2 lg:block">
                <p className="hidden font-mono text-[10px] tracking-[0.16em] text-[var(--text-muted)] lg:block">
                  {segment.code}
                </p>
                <p className="text-[13px] text-[var(--text-soft)] lg:mt-2 lg:text-xs lg:text-[var(--text-muted)]">
                  {segment.title}
                </p>
              </div>

              {isRefreshing && !lastCheckedAt ? (
                <>
                  <div className="h-3 w-24 animate-pulse rounded bg-[var(--surface-selected)] lg:mt-2.5 lg:h-4" />
                  <div className="h-3 w-16 animate-pulse rounded bg-[var(--border-subtle)] lg:mt-2" />
                </>
              ) : (
                <>
                  <p className="truncate text-right font-mono text-[11px] text-[var(--text-muted)] lg:mt-2 lg:text-left">
                    {segment.detail}
                  </p>
                  <div className="flex items-center justify-end gap-2 lg:mt-2.5 lg:justify-start">
                    <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', styles.dot)} aria-hidden="true" />
                    <strong className={cn('whitespace-nowrap text-[13px] font-bold lg:text-[15px]', styles.text)}>
                      {segment.label}
                    </strong>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
